/**
 * ll87Status - shared FR-12 legacy-status vocabulary for LL87 (Energy Audit &
 * Retro-Commissioning). PR-B (FR-25) maps the backend's 10-state cycle machine
 * onto 8 legacy `status` tokens, two of them new (`upcoming`, `overdue`).
 * Status strings are opaque tokens the FE maps, never keys it parses; the
 * owner-facing copy per token is `LL87_PILL_COPY` below.
 *
 *   cycle_state                 legacy status
 *   --------------------------  --------------
 *   covered_due_this_year       eligible
 *   covered_upcoming            upcoming (new)
 *   accruing_missed_cycle       overdue  (new)
 *   covered_cycle_unresolved    maybe
 *   deferred                    deferred
 *   extension_pending           eligible
 *   filed_awaiting_acceptance   filed
 *   accepted                    filed
 *   not_covered                 not_eligible
 *   unknown                     unknown        ← the ONLY state allowed to
 *                                                render the amber outage pill
 *
 * Five consumers key off the maps below via
 * `satisfies Record<Ll87LegacyStatus, …>`, so an unmapped token fails the build:
 *
 *   1. components/portal/StatusBadge.tsx        (LL87_PILL_COPY)
 *   2. pages/Portal/BuildingDetail/Page.tsx
 *      `statusToPillBucket`                     (LL87_STATUS_TO_PILL_BUCKET)
 *   3. pages/Portal/BuildingDetail/Page.tsx
 *      existence-override replacement           (same map - bucket truth)
 *   4. pages/Portal/Tracker/Page.tsx StatusIcon (LL87_STATUS_ICON_TONE)
 *   5. pages/Portal/BuildingDetail/Page.tsx
 *      analytics status mapping                 (LL87_STATUS_TO_ANALYTICS)
 *
 * camelCase boundary: status VALUES pass the axios interceptor untouched, so
 * `not_eligible` arrives verbatim; the FR-12 payload KEYS do camelize, so
 * consumers read `cycleState`, `hardshipExtensionDeadline`,
 * `dueYearUnderReview`.
 */
import type { CodeAssignmentStatus } from "@/analytics/events";
import { overdueTag } from "./overdueTag";
import { nyDate } from "@/components/portal/violationRemediation";
import {
  isLl87CycleState,
  type Ll87CycleState,
} from "@/components/portal/ll87CycleStates";

export const LL87_LEGACY_STATUSES = [
  "eligible",
  "upcoming",
  "overdue",
  "maybe",
  "deferred",
  "filed",
  "not_eligible",
  "unknown",
] as const;

export type Ll87LegacyStatus = (typeof LL87_LEGACY_STATUSES)[number];

/** The four filter-pill buckets on the portal BuildingDetail page. */
export type PillBucket = "eligible" | "maybe" | "unknown" | "not_eligible";

/**
 * FR-25.2 - pill bucketing. `overdue` is actionable (Required: an accruing
 * missed cycle is the most action-worthy row on the page); `upcoming` is
 * counted but non-actionable (Not Required, with `filed` / `deferred`).
 */
export const LL87_STATUS_TO_PILL_BUCKET = {
  eligible: "eligible",
  upcoming: "not_eligible",
  overdue: "eligible",
  maybe: "maybe",
  deferred: "not_eligible",
  filed: "not_eligible",
  not_eligible: "not_eligible",
  unknown: "unknown",
} as const satisfies Record<Ll87LegacyStatus, PillBucket>;

/**
 * FR-25.5 - analytics mapping, mirroring the pill bucketing at the analytics
 * boundary. `unknown` stays `undecided`: a data outage is not a compliance
 * posture. No fall-through to `undecided` for any mapped token.
 */
export const LL87_STATUS_TO_ANALYTICS = {
  eligible: "required",
  upcoming: "not_required",
  overdue: "required",
  maybe: "needs_review",
  deferred: "not_required",
  filed: "not_required",
  not_eligible: "not_required",
  unknown: "undecided",
} as const satisfies Record<Ll87LegacyStatus, CodeAssignmentStatus>;

/**
 * Normalized view of the FR-12 keys on the LL87 entry of the building-codes
 * payload. The interceptor camelizes keys but SSE / raw paths bypass it, so
 * every field reads BOTH spellings (the pattern BuildingDetail uses for
 * `confidence_band.cure_window`). VALUES are never transformed.
 */
export type Ll87StatusIconTone = "actionable" | "info" | "review" | "outage";

export const LL87_STATUS_ICON_TONE = {
  eligible: "actionable",
  upcoming: "info",
  overdue: "actionable",
  maybe: "review",
  deferred: "info",
  filed: "info",
  not_eligible: "info",
  unknown: "outage",
} as const satisfies Record<Ll87LegacyStatus, Ll87StatusIconTone>;

/** Type guard - is this wire token one of the LL87 legacy statuses? */
export const isLl87LegacyStatus = (
  status: string,
): status is Ll87LegacyStatus =>
  (LL87_LEGACY_STATUSES as readonly string[]).includes(status);

/**
 * FR-12/FR-13 `evidence_caveats` - machine tokens (`public_data_only`,
 * `doe_tool_timestamp_not_dob_receipt`, …). Owners NEVER see them verbatim
 * (FR-26): render through `EVIDENCE_CAVEAT_COPY`. Array-of-string VALUES
 * survive the interceptor untouched.
 */
export interface Ll87WireFields {
  /** FR-12 `cycle_state` (10-state machine token). */
  cycleState: string | null;
  /** Next assigned due year (`subcycle_year`). */
  dueYear: number | null;
  /** FR-7 deadline ISO (`YYYY-MM-DD`) - null when subcycle unresolved. */
  deadline: string | null;
  /** `hardship_extension_deadline` ISO - Oct 1 of the due year. */
  hardshipExtensionDeadline: string | null;
  /** That date T-1 (Leo 10-10), and DOB's Oct 1 named once when it moved. */
  hardshipShown?: string | null;
  hardshipNote?: string | null;
  /** FR-9 identity red flag - render the Needs-Review treatment. */
  dueYearUnderReview: boolean;
  /** `filing_evidence.filed_year` when positive evidence exists. */
  filedYear: number | null;
  /**
   * `filing_evidence.source` token (VALUE, verbatim): `nyc_ll87_filing_record`
   * (ETL row) or `owner_attestation` (staff-verified, FR-19a). Null when there
   * is no positive evidence, or on a payload predating the field.
   */
  filingEvidenceSource: string | null;
  /**
   * FR-12 `missed_cycle_year` - the year an `accruing_missed_cycle` building
   * failed to file for. PR-D prefills the FR-19b "I already filed" input.
   */
  missedCycleYear: number | null;
  /**
   * FR-12/FR-13 `evidence_caveats` - machine tokens (`public_data_only`,
   * `doe_tool_timestamp_not_dob_receipt`, …). Owners NEVER see these verbatim
   * (FR-26): render through `EVIDENCE_CAVEAT_COPY`. Array-of-string VALUES are
   * untouched by the interceptor, so tokens arrive as authored.
   */
  evidenceCaveats: string[];
}

type WireRecord = Record<string, unknown>;

const pick = <T>(obj: WireRecord, camel: string, snake: string): T | null => {
  const value = obj[camel] ?? obj[snake];
  return (value as T | undefined) ?? null;
};

export const readLl87WireFields = (code: unknown): Ll87WireFields => {
  const obj = (code ?? {}) as WireRecord;
  const evidence = (pick<WireRecord>(
    obj,
    "filingEvidence",
    "filing_evidence",
  ) ?? {}) as WireRecord;
  const rawCaveats = pick<unknown>(obj, "evidenceCaveats", "evidence_caveats");
  const evidenceCaveats = Array.isArray(rawCaveats)
    ? rawCaveats.filter((t): t is string => typeof t === "string")
    : [];
  return {
    cycleState: pick<string>(obj, "cycleState", "cycle_state"),
    dueYear: pick<number>(obj, "subcycleYear", "subcycle_year"),
    deadline: pick<string>(obj, "deadline", "deadline"),
    hardshipExtensionDeadline: pick<string>(
      obj,
      "hardshipExtensionDeadline",
      "hardship_extension_deadline",
    ),
    hardshipShown: pick<string>(obj, "hardshipShown", "hardship_shown"),
    hardshipNote: pick<string>(obj, "hardshipNote", "hardship_note"),
    dueYearUnderReview:
      pick<boolean>(obj, "dueYearUnderReview", "due_year_under_review") ===
      true,
    filedYear: pick<number>(evidence, "filedYear", "filed_year"),
    filingEvidenceSource: pick<string>(evidence, "source", "source"),
    missedCycleYear: pick<number>(obj, "missedCycleYear", "missed_cycle_year"),
    evidenceCaveats,
  };
};

// FR-26 - evidence-caveat owner-copy dictionary. The BE stamps
// `evidence_caveats` as opaque tokens defined once in backend
// `tracking/constants/evidence.py`; FR-26 acceptance is "tokens never appear
// verbatim". This is the single FE rendering map for every LL87 surface, and
// an unmapped token falls back to `null` - dropped, not leaked. Copy rule 8
// (estimated-vs-booked, never owed/billed; challenge-not-cure;
// RDP-files-not-CodeClear) applies: these strings are customer-visible.

/** Token `tracking/constants/evidence.py` CAVEAT_NO_CITY_TICKET. */
export const LL87_NO_CITY_TICKET = "no_city_ticket";

export const EVIDENCE_CAVEAT_COPY: Record<string, string> = {
  public_data_only:
    "Estimated from public records only — not a bill or an issued penalty.",
  // The blind window. Deadline-math / posting-delay framed (Leo 2026-07-17) -
  // never "public records end (in) 2024" / "data through 2024". The token KEY
  // stays in lockstep with the BE constant.
  public_records_end_2024:
    "Filed reports post to NYC's public record on a delay. We track this building continuously and confirm the moment your filing posts — nothing falls through.",
  confirm_filing_cta:
    "If an Energy Efficiency Report was already filed, you can confirm it and we'll update this.",
  // Q5 (Leo 10-31): an LL87 miss DOB has not ticketed. Designer, Nov 1: the card has no confirm control, so the
  // line asks for nothing.
  [LL87_NO_CITY_TICKET]: "No city ticket.",
  doe_tool_timestamp_not_dob_receipt:
    "Shown from NYC records — this reflects a submission timestamp, not a confirmed DOB acceptance.",
  evidence_layer_missing:
    "We couldn't check prior-filing history for this building yet — nothing is assumed accruing.",
  coverage_dispute_open:
    "Coverage for this building is disputed — treat any figure as potential and verify with DOF.",
  cross_lot_filing_unconfirmed:
    "A filing on a linked lot on a different tax block was found — it can't be attributed to this building without review.",
};

/**
 * FR-26 - render `evidence_caveats` tokens as owner-facing copy. Unknown
 * tokens are DROPPED, never leaked verbatim. Wire order is preserved so the
 * primary caveat (e.g. `public_data_only`) reads first.
 */
export const renderLl87EvidenceCaveats = (
  caveats: readonly string[],
): string[] =>
  caveats
    .map((token) => EVIDENCE_CAVEAT_COPY[token])
    .filter((copy): copy is string => typeof copy === "string");

// FR-26 - Add-Building LL87 diligence summary, from the DB-first `codes.ll87`
// FR-12 result. The BE deadline string is authoritative: render it, never
// re-derive Dec 31, so the digit-5 2026-03-31 override survives. The filing
// line is "per NYC records", never "accepted"; its absence is posting-delay
// framed, never a "public data through <year>" cutoff claim.

export interface Ll87Diligence {
  /** True when LL87 applies (any covered state); false when not
   *  required; null when coverage is unknown / under review. */
  covered: boolean | null;
  /** One-line coverage verdict headline. */
  coverageLabel: string;
  /** "Next LL87 due 2032 (deadline Dec 31, 2032)" - null when there's
   *  no forward date to show (not covered, or due year under review). */
  dueLabel: string | null;
  /** "Filed 2019 per NYC records" or the posting-delay-framed "No LL87 filing
   *  … yet" - null when coverage is unknown. */
  filingLabel: string | null;
  /** Owner-facing caveat lines (already de-tokenized). */
  caveats: string[];
}

/** Human month/day for a `YYYY-MM-DD` deadline, e.g. "Mar 31, 2026" (digit-5
 *  override) vs "Dec 31, 2032". Day-level LOCAL parse - never `new Date(iso)`,
 *  whose UTC shift moves the day west of Greenwich. Null on a malformed
 *  string. */
const formatDeadlineDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return null;
  return `${month} ${Number(m[3])}, ${m[1]}`;
};

/** Coverage states the FR-12 machine emits that mean "LL87 applies." */
const COVERED_CYCLE_STATES: ReadonlySet<string> = new Set([
  "covered_due_this_year",
  "covered_upcoming",
  "covered_cycle_unresolved",
  "accruing_missed_cycle",
  "deferred",
  "extension_pending",
  "filed_awaiting_acceptance",
  "accepted",
]);

export const buildLl87Diligence = (wire: Ll87WireFields): Ll87Diligence => {
  const caveats = renderLl87EvidenceCaveats(wire.evidenceCaveats);
  const { cycleState } = wire;

  // Coverage verdict. `unknown` is a data outage, never "not required".
  // `due_year_under_review` (FR-9) suppresses the authoritative due year.
  if (cycleState === "not_covered") {
    return {
      covered: false,
      coverageLabel: "LL87 not required for this building",
      dueLabel: null,
      filingLabel: null,
      caveats,
    };
  }
  if (cycleState == null || cycleState === "unknown") {
    return {
      covered: null,
      coverageLabel: "LL87 coverage still resolving — we'll confirm shortly",
      dueLabel: null,
      filingLabel: null,
      caveats,
    };
  }

  const covered = COVERED_CYCLE_STATES.has(cycleState) ? true : null;
  // No year is asserted while the identity is under review or the subcycle is
  // unresolved (FR-9 / covered_cycle_unresolved).
  // unresolved, we do NOT assert a year (FR-9 / covered_cycle_unresolved).
  let dueLabel: string | null = null;
  if (
    covered &&
    !wire.dueYearUnderReview &&
    cycleState !== "covered_cycle_unresolved" &&
    wire.dueYear != null
  ) {
    const deadlinePretty = formatDeadlineDate(wire.deadline);
    dueLabel = deadlinePretty
      ? `Next LL87 due ${wire.dueYear} (deadline ${deadlinePretty})`
      : `Next LL87 due ${wire.dueYear}`;
  } else if (covered && wire.dueYearUnderReview) {
    dueLabel = "Next LL87 due year under review";
  }

  // "Filed <year> per NYC records" from ETL/attestation, never "accepted";
  // otherwise posting-delay framed (Leo 2026-07-17), never a "public data
  // through <year>" cutoff claim. Tier-1 pitfall #4: buyers inherit
  // five-figure accruals, so the ABSENCE of a filing is the point. The due
  // date is NOT repeated here - `dueLabel` carries the authoritative BE
  // deadline, and a hardcoded "December 31" would contradict it.
  const filingLabel =
    wire.filedYear != null
      ? `Filed ${wire.filedYear} per NYC records`
      : "No LL87 filing on NYC's public record yet — filed reports post on a delay; we'll confirm the moment one posts.";

  return {
    covered,
    coverageLabel: covered
      ? "LL87 applies to this building"
      : "LL87 coverage still resolving — we'll confirm shortly",
    dueLabel,
    filingLabel,
    caveats,
  };
};

/**
 * Normalized view of the PR-B exposure / evidence keys on the LL87 entry
 * (FR-12 / FR-13 / FR-35, stamped at
 * backend/server/tracking/services/ll87_eligibility.py:427-446). Additive
 * sibling of `Ll87WireFields`; same snake-or-camel reads, opaque VALUES.
 */
export interface Ll87ExposureFields {
  /** §103-07(n) asserted accrued exposure (lower bound), whole USD. */
  accruedExposureUsd: number;
  /** Contingent high (O-10 range top) - equals asserted unless the
   *  deadline basis is extended/administrative. */
  accruedExposureHighUsd: number;
  /** FR-35 tier token (`public_data_only` only in PR-B) or null. */
  evidenceTier: string | null;
  /** FR-13 caveat tokens (e.g. `doe_tool_timestamp_not_dob_receipt`). */
  evidenceCaveats: string[];
  /** FR-21/FR-29 basis token: `statutory_dec31` / `sn_2026_03_31` /
   *  `extension`, or null. */
  deadlineBasis: string | null;
  /** The asserted missed compliance year (accruing state only). */
  missedCycleYear: number | null;
  /** ISO `YYYY-MM-DD` accrual start (day after the missed deadline). */
  accrualStart: string | null;
  /** Coverage verdict token: `covered` / `not_covered` / `unknown`. */
  coverage: string | null;
  /** FR-6 derivation source token (`cbl_col_h`, `gfa_derived`, …). */
  coverageSource: string | null;
  /** FR-5 divergence flag - sources disagree; we flag, never flip. */
  cblDivergenceFlag: boolean;
}

export const readLl87ExposureFields = (code: unknown): Ll87ExposureFields => {
  const obj = (code ?? {}) as WireRecord;
  const caveats = pick<unknown[]>(obj, "evidenceCaveats", "evidence_caveats");
  return {
    accruedExposureUsd:
      pick<number>(obj, "accruedExposureUsd", "accrued_exposure_usd") ?? 0,
    accruedExposureHighUsd:
      pick<number>(
        obj,
        "accruedExposureHighUsd",
        "accrued_exposure_high_usd",
      ) ?? 0,
    evidenceTier: pick<string>(obj, "evidenceTier", "evidence_tier"),
    evidenceCaveats: Array.isArray(caveats)
      ? caveats.filter((c): c is string => typeof c === "string")
      : [],
    deadlineBasis: pick<string>(obj, "deadlineBasis", "deadline_basis"),
    missedCycleYear: pick<number>(obj, "missedCycleYear", "missed_cycle_year"),
    accrualStart: pick<string>(obj, "accrualStart", "accrual_start"),
    coverage: pick<string>(obj, "coverage", "coverage"),
    coverageSource: pick<string>(obj, "coverageSource", "coverage_source"),
    cblDivergenceFlag:
      pick<boolean>(obj, "cblDivergenceFlag", "cbl_divergence_flag") === true,
  };
};

/**
 * Context StatusBadge needs for the LL87-aware FR-12 labels. All fields are
 * camelized wire keys off the building-codes payload.
 */
export interface Ll87BadgeContext {
  /** FR-12 `cycle_state` (10-state machine token), when known. */
  cycleState?: string | null;
  /** Next assigned due year - drives "Upcoming — due <year>". */
  dueYear?: number | null;
  /** `filing_evidence.filed_year` - drives "Filed <year> per NYC records". */
  filedYear?: number | null;
  /**
   * FR-7 governing deadline ISO - drives the full "Due — file by <deadline>"
   * label on `eligible` where there is room (frame 17 planner rows, frame 18
   * vault). Callers that omit it (the 120px StatusBadge column) keep "Required".
   */
  deadline?: string | null;
}

const MONTHS_SHORT_LL87 = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * "Dec 31, 2026" from a BE `YYYY-MM-DD` - split, never `new Date(iso)` (the
 * April-30-for-May-1 UTC off-by-one; house idiom, Ll87RowChips.tsx /
 * Flow/format.ts `parseIsoDay`).
 */
export const ll87ShortDate = (iso: string): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = MONTHS_SHORT_LL87[Number(m[2]) - 1];
  if (!month) return null;
  return `${month} ${Number(m[3])}, ${m[1]}`;
};

export interface Ll87PillCopy {
  /**
   * Short pill text - must fit the 120px fixed-width status column (10px
   * JetBrains Mono ≈ 6px/char + 16px padding → ~17 chars max).
   */
  label: string;
  /**
   * Full FR-12 owner-facing copy. Rides `title` + aria, the same
   * short-label/full-title pattern the filing-stage badge uses for that column.
   */
  fullLabel: string;
}

/**
 * FR-25.1 - LL87-aware pill copy per legacy token. Exhaustive: a token without
 * copy fails the build. StatusBadge consults this only when the caller passes
 * LL87 context.
 */
export const LL87_PILL_COPY = {
  eligible: (ctx: Ll87BadgeContext) => {
    // FR-12 vocabulary: "Due — file by <deadline>" (frame 17, verbatim).
    // `extension_pending` rides the same token with the EXTENDED date as the
    // governing deadline → "Due <date>". Short label stays "Required".
    const due = ctx.deadline ? ll87ShortDate(ctx.deadline) : null;
    if (!due) return { label: "Required", fullLabel: "Required" };
    return {
      label: "Required",
      fullLabel:
        ctx.cycleState === "extension_pending"
          ? `Due ${due}`
          : `Due — file by ${due}`,
    };
  },
  upcoming: (ctx: Ll87BadgeContext) => ({
    label: ctx.dueYear != null ? `Upcoming ${ctx.dueYear}` : "Upcoming",
    fullLabel:
      ctx.dueYear != null
        ? `Upcoming — due ${ctx.dueYear}`
        : "Upcoming — due year to be determined",
  }),
  overdue: () => ({
    label: "Action Needed",
    fullLabel: "Action Needed",
  }),
  maybe: () => ({
    label: "Needs Review",
    fullLabel: "Needs Review — due year TBD",
  }),
  deferred: () => ({
    // "Deferral Filed", never the bare "Deferred" that StatusBadge documents
    // as LL97-CP2's pre-binding state (review R3-S2). An LL87 deferral is a
    // legal state: EER1 is on file with DOB.
    label: "Deferral Filed",
    fullLabel: "Deferral Filed",
  }),
  filed: (ctx: Ll87BadgeContext) => {
    if (ctx.cycleState === "filed_awaiting_acceptance") {
      return {
        label: "Filed",
        fullLabel: "Filed — DOB acceptance pending",
      };
    }
    // `accepted` (or unknown sub-state). "per NYC records" is the FR-13
    // softener: the ETL timestamp is a DOE-tool stamp, not a DOB receipt.
    return {
      label: ctx.filedYear != null ? `Filed ${ctx.filedYear}` : "Filed",
      fullLabel:
        ctx.filedYear != null
          ? `Filed ${ctx.filedYear} per NYC records`
          : "Filed per NYC records",
    };
  },
  not_eligible: () => ({
    label: "Not Required",
    fullLabel: "Not Required",
  }),
  unknown: () => ({
    label: "Unavailable",
    fullLabel: "Data Unavailable",
  }),
} as const satisfies Record<
  Ll87LegacyStatus,
  (ctx: Ll87BadgeContext) => Ll87PillCopy
>;

// PR-D redesign (frame 01, "LL87 — THE entry row") - the three LL87-only
// enrichments under the BuildingDetail code label: a CYCLE eyebrow, a
// risk-med days-left / statutory deadline subline, and a 12–18-month lead-time
// nudge chip. Pure typed helpers, so the row JSX stays thin and testable.

/**
 * Cycle eyebrow, e.g. "LL87 · CYCLE 2026" (frame 01 `.row-eyebrow`). Falls
 * back to "LL87 · CYCLE TBD" when the subcycle year is unresolved (FR-9 /
 * `covered_cycle_unresolved`) - never assert a year we do not have.
 */
export const ll87CycleEyebrow = (dueYear: number | null): string =>
  `LL87 · CYCLE ${dueYear != null ? dueYear : "TBD"}`;

/**
 * Days-left readout for the risk-med subline. Past the deadline it flips to
 * `overdueTag` ("+2y overdue"); the exact-day boundary reads "Due today", not
 * "0 days left". Day-granularity LOCAL parse - never `new Date(iso)` (UTC off-by-one;
 * Ll87RowChips.parseIsoDay). Null when there is no resolvable deadline, so the
 * caller renders the bare statutory line without a phantom count.
 */
export interface Ll87DaysLeft {
  /** e.g. "175 days left" / "+2y overdue" / "Due today". */
  text: string;
  /** True once today is past the deadline. Drives no styling today (the
   *  subline is always risk-med per frame 01); exposed for callers/tests. */
  overdue: boolean;
}

/** Whole days from New York's today (`nyDate`, Leo 10-12) to an ISO date; negative once past. */
export const daysUntilIsoDay = (
  iso: string | null | undefined,
  today: Date = new Date(),
): number | null => {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!m) return null;
  const day = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round((day - Date.parse(nyDate(today))) / 86_400_000);
};

export const ll87DaysLeft = (
  deadlineIso: string | null,
  today: Date = new Date(),
): Ll87DaysLeft | null => {
  const diffDays = daysUntilIsoDay(deadlineIso, today);
  if (diffDays === null) return null;
  if (diffDays === 0) return { text: "Due today", overdue: false };
  if (diffDays > 0) {
    return {
      text: `${diffDays} ${diffDays === 1 ? "day" : "days"} left`,
      overdue: false,
    };
  }
  return { text: `${overdueTag(-diffDays)} overdue`, overdue: true };
};

/**
 * Overdue-readout gate. Once the cycle state says a filing exists
 * (`filed_awaiting_acceptance` / `accepted`) or the cycle is legally resolved
 * (`deferred`), "+2y overdue" beside the Filed/Deferral pill contradicts
 * it. Positive counts are unaffected. Backend
 * `chat/carbon_law_registry._ll87_row` nulls the same negative countdown, so
 * the two surfaces cannot disagree. Exhaustive via `satisfies`.
 */
const LL87_OVERDUE_READOUT_BY_CYCLE_STATE = {
  covered_upcoming: true,
  covered_due_this_year: true,
  covered_cycle_unresolved: true,
  accruing_missed_cycle: true, // the honest accruing message
  extension_pending: true,
  deferred: false,
  filed_awaiting_acceptance: false, // "Filed" pill + "overdue" contradiction
  accepted: false,
  not_covered: false,
  unknown: false,
} as const satisfies Record<Ll87CycleState, boolean>;

/**
 * Backend-mirrored lead-time window (audit `UPCOMING_WINDOW_MONTHS = 24`): a
 * covered obligation is INSIDE the filing window when its deadline is within
 * 24 months or already reached. Beyond that the row must read calm - a due
 * YEAR, not a live day-count ("1265 days left") or an "engage professionals
 * now" nudge. Mirrors the sync gate that refuses to mint a filing row outside
 * the window.
 */
export const LL87_FILING_WINDOW_MONTHS = 24;

export const isLl87WithinFilingWindow = (
  deadlineIso: string | null,
  today: Date = new Date(),
): boolean => {
  if (!deadlineIso) return true; // unresolved date → never suppress
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(deadlineIso);
  if (!m) return true;
  // Day-granular local parse (never `new Date(iso)`). `month + 24` lets the
  // Date constructor roll the year.
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const horizon = new Date(
    today.getFullYear(),
    today.getMonth() + LL87_FILING_WINDOW_MONTHS,
    today.getDate(),
  );
  return due.getTime() <= horizon.getTime();
};

export const ll87DeadlineReadout = (
  deadlineIso: string | null,
  cycleState: string | null,
  today: Date = new Date(),
): Ll87DaysLeft | null => {
  const readout = ll87DaysLeft(deadlineIso, today);
  if (!readout) return null;
  // Beyond the 24-month window a live day-count reads as false urgency:
  // suppress it so the row shows the due YEAR plus the bare statutory line.
  // Overdue counts are handled by the cycle-state gate below.
  if (!readout.overdue && !isLl87WithinFilingWindow(deadlineIso, today)) {
    return null;
  }
  if (
    readout.overdue &&
    cycleState != null &&
    isLl87CycleState(cycleState) &&
    !LL87_OVERDUE_READOUT_BY_CYCLE_STATE[cycleState]
  ) {
    return null;
  }
  return readout;
};
/**
 * The literal §28-308.4 citation the statutory-deadline subline carries (frame
 * 01 `.cite`), so the row JSX and the tests share one string.
 */
export const LL87_STATUTORY_DEADLINE_CITE = "§28-308.4";

/** The lead-time nudge copy (Mix board text; no em-dash in customer copy). */
export const LL87_LEAD_TIME_NUDGE =
  "12–18 month lead time. Engage professionals now";

/**
 * FR-25 lead-time nudge gate: fires when LL87 is covered AND the owner has not
 * engaged a professional or filed, where the 12–18-month audit lead time is
 * the actionable message. Suppressed on a resolved track, when not covered, or
 * on a data outage. Exhaustive over the 10-state machine via `satisfies`.
 */
const LL87_LEAD_TIME_NUDGE_BY_CYCLE_STATE = {
  covered_upcoming: true,
  covered_due_this_year: true,
  covered_cycle_unresolved: true,
  accruing_missed_cycle: true,
  extension_pending: true,
  deferred: false,
  filed_awaiting_acceptance: false,
  accepted: false,
  not_covered: false,
  unknown: false,
} as const satisfies Record<Ll87CycleState, boolean>;

export const shouldShowLl87LeadTimeNudge = (
  cycleState: string | null,
  deadlineIso: string | null = null,
  today: Date = new Date(),
): boolean => {
  if (cycleState == null || !isLl87CycleState(cycleState)) return false;
  if (!LL87_LEAD_TIME_NUDGE_BY_CYCLE_STATE[cycleState]) return false;
  // Suppress "engage professionals now" BEYOND the 24-month window. An
  // unresolved/absent deadline keeps the nudge: that is a current-cycle
  // ambiguity, not a far-future date.
  return isLl87WithinFilingWindow(deadlineIso, today);
};

/**
 * Row-level twin of the reactive `outside_filing_window` sync notice: the calm
 * "nothing to do yet" copy on a far-future covered/upcoming row, where the
 * nudge is suppressed for distance, not state. Requires a real deadline beyond
 * the window; a null deadline is a current-cycle ambiguity.
 */
export const ll87NotYetNote = (dueYear: number | null): string =>
  dueYear != null
    ? `Filing opens closer to the due year. Next compliance year is ${dueYear}. Nothing to do yet.`
    : "Filing opens closer to the due year. Nothing to do yet.";

export const shouldShowLl87NotYet = (
  cycleState: string | null,
  deadlineIso: string | null,
  today: Date = new Date(),
): boolean => {
  if (cycleState == null || !isLl87CycleState(cycleState)) return false;
  if (!LL87_LEAD_TIME_NUDGE_BY_CYCLE_STATE[cycleState]) return false;
  // A missed cycle accrues now; its far `deadline` is the NEXT cycle (Gateway,
  // 2026-10-01: "Nothing to do yet" under ACTION NEEDED $13,000).
  if (cycleState === "accruing_missed_cycle") return false;
  if (!deadlineIso) return false; // no real far date → not "far future"
  return !isLl87WithinFilingWindow(deadlineIso, today);
};
